import React, { Component } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Grid } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoan, ILoanAccount, ILoanPayment } from '../../../interfaces';
import { Icon, Loader } from '../../common';
import { SessionStoreClass } from '../../../stores';
import SmartBool from '../../../utils/SmartBool';

import LoanPaymentsTable from './LoanPaymentsTable';
import LogLoanPaymentModal from './LogLoanPaymentModal';

const { LOAN_PAYMENT_TYPE_BALANCE, PERMISSIONS } = AppConstants;

interface IProps {
  loanAccount: ILoanAccount;
  // Logging a payment writes to the account's activity feed, which this tab doesn't own
  onPaymentLogged: () => void;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * Payments against the account's loans, and the one place they are logged.
 *
 * Separate from the Compliance tab, which handles the quarterly charge levied on the account as a
 * whole: a compliance payment settles a charge and touches no loan balance, while everything here
 * is applied to loan principal and interest through the waterfall.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class TabPayments extends Component<IProps> {
  @observable private isLoading = true;
  @observable private loans: ILoan[] = [];
  @observable private payments: ILoanPayment[] = [];
  @observable private showLogPaymentModal = new SmartBool();

  public componentDidMount () {
    this.fetchPaymentData();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchPaymentData ({ isRefresh = false } = {}) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    // A refresh leaves the table up while it runs. Every logged payment goes through one, and
    // swapping the table for a full-height loader makes the page jump for the round trip.
    this.isLoading = !isRefresh;

    try {
      // The loans come along to name what each payment was applied to, and to know whether there is
      // anything to pay at all.
      const [loans, payments] = await Promise.all([
        Clients.loans.listAll({ params: { account: loanAccount.id } }),
        Clients.loanPayments.listAll({ params: { account: loanAccount.id, type: LOAN_PAYMENT_TYPE_BALANCE } }),
      ]);

      this.loans = loans;
      this.payments = payments;
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the account payments:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  private async handlePaymentLogged () {
    // Re-read rather than patching the row in place: a payment moves the balances the API works out.
    await this.fetchPaymentData({ isRefresh: true });
    this.props.onPaymentLogged();
  }

  /**
   * react-table wants plain arrays, not the observable ones mobx made of the responses.
   *
   * Cloned here rather than in each place they are handed down, so that the arrays keep the same
   * identity between renders -- see the same note on TabCompliance.
   */
  @computed
  private get loanData () {
    return toJS(this.loans);
  }

  @computed
  private get paymentData () {
    return toJS(this.payments);
  }

  private renderLogPaymentButton () {
    const { SessionStore } = this.injected;

    // Logging is rejected without the add permission, so only offer it to users who have it
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_PAYMENT)) { return null; }

    // Every payment here is allocated to a loan, so there is nothing to log until the account has one
    if (!this.loans.length) { return null; }

    return (
      <Button bsStyle='primary' className='btn-log-loan-payment' onClick={this.showLogPaymentModal.setTrue}>
        <Icon type='usd' /> Log payment
      </Button>
    );
  }

  private renderContent () {
    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    return (
      <LoanPaymentsTable
        loans={this.loanData}
        newButton={this.renderLogPaymentButton()}
        payments={this.paymentData}
      />
    );
  }

  public render () {
    const { loanAccount } = this.props;

    return (
      <Grid fluid className='loan-account-detail-tab tab-payments'>
        {this.renderContent()}

        {this.showLogPaymentModal.isTrue && (
          <LogLoanPaymentModal
            loanAccount={loanAccount}
            loans={this.loanData}
            onClose={this.showLogPaymentModal.setFalse}
            onCreate={this.handlePaymentLogged}
          />
        )}
      </Grid>
    );
  }
}

export default TabPayments;
